import { useState, useEffect, useCallback, useRef } from 'react';
import { getFirestore, collection, collectionGroup, doc, getDoc, getDocs, addDoc, updateDoc, setDoc, orderBy, query, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { app } from '../../firebase/firebaseConfig';

export function useAdminSupportMessages(accountUUID) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [conversationData, setConversationData] = useState(null);

  const db = getFirestore(app);
  const auth = getAuth(app);

  // Refs to track subscriptions
  const conversationUnsubRef = useRef(null);
  const messagesUnsubRef = useRef(null);

  // Subscribe to conversation and messages when accountUUID is available
  useEffect(() => {
    if (!accountUUID) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Subscribe to conversation document
    const conversationRef = doc(db, 'supportConversations', accountUUID);
    conversationUnsubRef.current = onSnapshot(
      conversationRef,
      (docSnap) => {
        if (docSnap.exists()) {
          setConversationData({ id: docSnap.id, ...docSnap.data() });
        } else {
          setConversationData(null);
        }
      },
      (err) => {
        console.error('Error subscribing to conversation:', err);
        setError('Failed to load conversation');
      }
    );

    // Subscribe to messages
    const messagesRef = collection(db, 'supportConversations', accountUUID, 'messages');
    const messagesQuery = query(messagesRef, orderBy('createdAt', 'asc'));

    messagesUnsubRef.current = onSnapshot(
      messagesQuery,
      (snapshot) => {
        const messagesList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.() || new Date()
        }));
        setMessages(messagesList);
        setLoading(false);
      },
      (err) => {
        console.error('Error subscribing to messages:', err);
        // Only show error if it's not a permission issue on empty collection
        if (err.code !== 'permission-denied') {
          setError('Failed to load messages');
        }
        setLoading(false);
      }
    );

    // Cleanup subscriptions on unmount or accountUUID change
    return () => {
      if (conversationUnsubRef.current) {
        conversationUnsubRef.current();
      }
      if (messagesUnsubRef.current) {
        messagesUnsubRef.current();
      }
    };
  }, [db, accountUUID]);

  // Returns true when the message reached Firestore, false otherwise, so the
  // composer can restore the text into the draft on failure instead of losing it.
  const sendMessage = useCallback(async (content, images = [], preGeneratedId = null) => {
    const trimmedContent = content.trim();
    if ((!trimmedContent && images.length === 0) || !accountUUID) return false;

    setSending(true);
    setError(null);

    // Compute preview for conversation doc
    let preview;
    if (trimmedContent) {
      preview = trimmedContent.substring(0, 100);
      if (images.length > 0) preview += ` [+${images.length} image${images.length > 1 ? 's' : ''}]`;
    } else {
      preview = images.length === 1 ? '[Image]' : `[${images.length} Images]`;
    }

    // Optimistic update
    const tempId = 'temp-' + Date.now();
    const optimisticMessage = {
      id: tempId,
      content: trimmedContent,
      senderType: 'admin',
      senderEmail: auth.currentUser?.email,
      createdAt: new Date(),
      readAt: null,
      pending: true,
      ...(images.length > 0 && { images })
    };
    setMessages(prev => [...prev, optimisticMessage]);

    try {
      const user = auth.currentUser;
      const conversationRef = doc(db, 'supportConversations', accountUUID);

      // Update conversation document
      const conversationSnap = await getDoc(conversationRef);
      if (conversationSnap.exists()) {
        await updateDoc(conversationRef, {
          lastMessageAt: serverTimestamp(),
          lastMessagePreview: preview,
          unreadByUser: (conversationSnap.data().unreadByUser || 0) + 1,
          unreadByAdmin: 0,
          status: 'open'
        });
      }

      // Build message document
      const messageData = {
        content: trimmedContent,
        senderType: 'admin',
        senderEmail: user.email,
        createdAt: serverTimestamp(),
        readAt: null,
        ...(images.length > 0 && { images })
      };

      // Add message (use pre-generated ID if provided)
      const messagesRef = collection(db, 'supportConversations', accountUUID, 'messages');
      if (preGeneratedId) {
        await setDoc(doc(messagesRef, preGeneratedId), messageData);
      } else {
        await addDoc(messagesRef, messageData);
      }

      // Remove optimistic message (real one will come via onSnapshot)
      setMessages(prev => prev.filter(m => m.id !== tempId));
      return true;

    } catch (err) {
      console.error('Error sending message:', err);
      // Remove optimistic message on error
      setMessages(prev => prev.filter(m => m.id !== tempId));
      setError('Failed to send message. Please try again.');
      return false;
    } finally {
      setSending(false);
    }
  }, [db, auth, accountUUID]);

  const markAsRead = useCallback(async () => {
    if (!accountUUID) return;

    try {
      const conversationRef = doc(db, 'supportConversations', accountUUID);
      const conversationSnap = await getDoc(conversationRef);

      if (conversationSnap.exists() && conversationSnap.data().unreadByAdmin > 0) {
        // Update conversation unread count
        await updateDoc(conversationRef, {
          unreadByAdmin: 0,
          lastReadByAdminAt: serverTimestamp()
        });

        // Also mark individual user messages as read (for double ticks)
        const messagesRef = collection(db, 'supportConversations', accountUUID, 'messages');
        const messagesQuery = query(messagesRef, orderBy('createdAt', 'asc'));
        const messagesSnap = await getDocs(messagesQuery);

        const now = serverTimestamp();
        for (const messageDoc of messagesSnap.docs) {
          const messageData = messageDoc.data();
          // Only mark user messages that haven't been read yet
          if (messageData.senderType === 'user' && !messageData.readAt) {
            await updateDoc(doc(db, 'supportConversations', accountUUID, 'messages', messageDoc.id), {
              readAt: now
            });
          }
        }
      }
    } catch (err) {
      console.error('Error marking messages as read:', err);
    }
  }, [db, accountUUID]);

  const updateStatus = useCallback(async (newStatus) => {
    if (!accountUUID) return;

    try {
      const conversationRef = doc(db, 'supportConversations', accountUUID);
      await updateDoc(conversationRef, { status: newStatus });
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update status');
    }
  }, [db, accountUUID]);

  return {
    messages,
    loading,
    error,
    sending,
    conversationData,
    sendMessage,
    markAsRead,
    updateStatus,
    clearError: () => setError(null)
  };
}

// Every draft the *current* admin has saved, keyed by accountUUID. Feeds the
// blue draft indicator on the account rows and the inbox list.
//
// The query is an UNFILTERED collection-group listen: Firestore serves that
// from automatic indexes, whereas adding a where() clause would require a
// collection-group-scoped single-field index to be configured. Other admins'
// drafts are filtered out client-side by document id (the doc id is the admin's
// auth uid). The result set is tiny — one small text document per account this
// admin has a draft for.
export function useAllAdminDrafts() {
  const [drafts, setDrafts] = useState({});
  const [uid, setUid] = useState(null);

  const db = getFirestore(app);
  const auth = getAuth(app);
  const unsubRef = useRef(null);

  // Track auth rather than reading auth.currentUser once — on a cold load the
  // dashboard can mount before Firebase has restored the session.
  useEffect(() => {
    return onAuthStateChanged(auth, (user) => setUid(user?.uid || null));
  }, [auth]);

  useEffect(() => {
    if (!uid) {
      setDrafts({});
      return;
    }

    unsubRef.current = onSnapshot(
      collectionGroup(db, 'adminDrafts'),
      (snapshot) => {
        const map = {};
        snapshot.docs.forEach(d => {
          if (d.id !== uid) return; // another admin's draft
          const data = d.data();
          const accountUUID = data.accountUUID || d.ref.parent.parent?.id;
          if (!accountUUID || !data.content) return;
          map[accountUUID] = {
            content: data.content,
            updatedAt: data.updatedAt?.toDate?.() || null
          };
        });
        setDrafts(map);
      },
      (err) => {
        console.error('Error subscribing to admin drafts:', err);
      }
    );

    return () => {
      if (unsubRef.current) unsubRef.current();
    };
  }, [db, uid]);

  const hasDraftForAccount = useCallback(
    (accountUUID) => Boolean(drafts[accountUUID]),
    [drafts]
  );

  return { drafts, hasDraftForAccount };
}

export function useAllConversations() {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const db = getFirestore(app);
  const unsubRef = useRef(null);

  // Subscribe to all conversations with real-time updates
  useEffect(() => {
    setLoading(true);
    setError(null);

    const conversationsRef = collection(db, 'supportConversations');

    unsubRef.current = onSnapshot(
      conversationsRef,
      (snapshot) => {
        const conversationsList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          lastMessageAt: doc.data().lastMessageAt?.toDate?.() || null,
          createdAt: doc.data().createdAt?.toDate?.() || null
        }));

        // Sort by lastMessageAt descending (most recent first)
        conversationsList.sort((a, b) => {
          const aTime = a.lastMessageAt?.getTime() || 0;
          const bTime = b.lastMessageAt?.getTime() || 0;
          return bTime - aTime;
        });

        setConversations(conversationsList);
        setLoading(false);
      },
      (err) => {
        console.error('Error subscribing to conversations:', err);
        setError('Failed to load conversations');
        setLoading(false);
      }
    );

    return () => {
      if (unsubRef.current) {
        unsubRef.current();
      }
    };
  }, [db]);

  const fetchConversations = useCallback(async () => {
    // Manual refresh if needed
    try {
      const conversationsRef = collection(db, 'supportConversations');
      const conversationsSnap = await getDocs(conversationsRef);

      const conversationsList = conversationsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        lastMessageAt: doc.data().lastMessageAt?.toDate?.() || null,
        createdAt: doc.data().createdAt?.toDate?.() || null
      }));

      conversationsList.sort((a, b) => {
        const aTime = a.lastMessageAt?.getTime() || 0;
        const bTime = b.lastMessageAt?.getTime() || 0;
        return bTime - aTime;
      });

      setConversations(conversationsList);
    } catch (err) {
      console.error('Error fetching conversations:', err);
    }
  }, [db]);

  const getUnreadCountForAccount = useCallback((accountUUID) => {
    const conversation = conversations.find(c => c.id === accountUUID);
    return conversation?.unreadByAdmin || 0;
  }, [conversations]);

  const getConversationStatus = useCallback((accountUUID) => {
    const conversation = conversations.find(c => c.id === accountUUID);
    return conversation?.status || null;
  }, [conversations]);

  return {
    conversations,
    loading,
    error,
    fetchConversations,
    getUnreadCountForAccount,
    getConversationStatus
  };
}
